import React, { useRef, useEffect, useState, useMemo } from 'react';
import * as d3 from 'd3';

const MARGIN = { top: 20, right: 30, bottom: 50, left: 55 };
const CHART_HEIGHT = 350;
const CHART_WIDTH = 700;

const SEVERITY_COLORS = {
  low: '#f59e0b',
  medium: '#f97316',
  high: '#dc2626',
  critical: '#7f1d1d',
};

const TYPE_LABELS = {
  spike: 'Spike',
  drop: 'Drop',
  outlier: 'Outlier',
  missing: 'Missing',
};

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  header: {
    marginBottom: '12px',
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '2px',
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    marginBottom: '0',
  },
  filterBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '12px',
    flexWrap: 'wrap',
  },
  filterLabel: {
    fontSize: '12px',
    fontWeight: 600,
    color: '#6b7280',
    marginRight: '4px',
  },
  filterButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 10px',
    borderRadius: '999px',
    fontSize: '11px',
    fontWeight: 600,
    cursor: 'pointer',
    border: '1px solid #e5e7eb',
    background: '#f9fafb',
    color: '#6b7280',
    transition: 'all 0.15s ease',
    userSelect: 'none',
  },
  filterButtonActive: {
    background: '#1f2937',
    color: '#fff',
    borderColor: '#1f2937',
  },
  summaryBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '12px',
    flexWrap: 'wrap',
  },
  summaryPill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
    padding: '3px 10px',
    borderRadius: '999px',
    fontSize: '11px',
    fontWeight: 600,
  },
  container: {
    overflowX: 'auto',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '8px 12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    maxWidth: '280px',
  },
  tooltipRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '16px',
    marginBottom: '2px',
  },
  tooltipLabel: {
    color: '#6b7280',
  },
  tooltipValue: {
    fontWeight: 600,
    color: '#1f2937',
  },
  emptyState: {
    color: '#6b7280',
    textAlign: 'center',
    padding: '40px 0',
  },
};

function formatDateLabel(dateStr) {
  const date = new Date(dateStr);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[date.getMonth()]} ${date.getDate()}`;
}

function formatDateFull(dateStr) {
  const date = new Date(dateStr);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
}

function getStrokeDasharray(type) {
  switch (type) {
    case 'outlier':
      return '6,3';
    case 'missing':
      return '2,2';
    default:
      return 'none';
  }
}

export default function PriceAnomalyTimeline({ data }) {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);
  const [activeTypes, setActiveTypes] = useState({
    spike: true,
    drop: true,
    outlier: true,
    missing: true,
  });

  const filteredAnomalies = useMemo(() => {
    if (!data || !data.anomalies) return [];
    return data.anomalies.filter((a) => activeTypes[a.type]);
  }, [data, activeTypes]);

  const severityCounts = useMemo(() => {
    if (!data || !data.anomalies) return { low: 0, medium: 0, high: 0, critical: 0 };
    const counts = { low: 0, medium: 0, high: 0, critical: 0 };
    data.anomalies.forEach((a) => {
      if (counts[a.severity] !== undefined) {
        counts[a.severity]++;
      }
    });
    return counts;
  }, [data]);

  const toggleType = (type) => {
    setActiveTypes((prev) => ({ ...prev, [type]: !prev[type] }));
  };

  useEffect(() => {
    if (!data || !data.dates || data.dates.length === 0 || !data.priceLine || data.priceLine.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const containerWidth = containerRef.current
      ? containerRef.current.clientWidth
      : CHART_WIDTH;
    const width = Math.max(600, containerWidth);
    const innerW = width - MARGIN.left - MARGIN.right;
    const innerH = CHART_HEIGHT - MARGIN.top - MARGIN.bottom;

    svg
      .attr('viewBox', `0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`)
      .attr('width', '100%')
      .attr('height', CHART_HEIGHT)
      .style('overflow', 'visible');

    const g = svg.append('g').attr('transform', `translate(${MARGIN.left}, ${MARGIN.top})`);

    const parsedDates = data.dates.map((d) => new Date(d));

    // X scale
    const x = d3.scaleTime()
      .domain(d3.extent(parsedDates))
      .range([0, innerW]);

    // Y scale — combine price line and anomaly prices
    const allPrices = [...data.priceLine];
    filteredAnomalies.forEach((a) => {
      if (a.price != null) allPrices.push(a.price);
    });
    const yMin = d3.min(allPrices) || 0;
    const yMax = d3.max(allPrices) || 100;
    const yPad = (yMax - yMin) * 0.15 || 5;

    const y = d3.scaleLinear()
      .domain([Math.max(0, yMin - yPad), yMax + yPad])
      .nice()
      .range([innerH, 0]);

    // Grid lines
    const yTicks = y.ticks(6);
    g.selectAll('.grid-line')
      .data(yTicks)
      .enter()
      .append('line')
      .attr('x1', 0)
      .attr('x2', innerW)
      .attr('y1', (d) => y(d))
      .attr('y2', (d) => y(d))
      .attr('stroke', '#f3f4f6')
      .attr('stroke-width', 1);

    // Area fill under price line
    const areaGen = d3.area()
      .x((d, i) => x(parsedDates[i]))
      .y0(innerH)
      .y1((d) => y(d))
      .defined((d) => d != null)
      .curve(d3.curveMonotoneX);

    g.append('path')
      .datum(data.priceLine)
      .attr('d', areaGen)
      .attr('fill', '#e5e7eb')
      .attr('fill-opacity', 0.35);

    // Price line
    const lineGen = d3.line()
      .x((d, i) => x(parsedDates[i]))
      .y((d) => y(d))
      .defined((d) => d != null)
      .curve(d3.curveMonotoneX);

    g.append('path')
      .datum(data.priceLine)
      .attr('d', lineGen)
      .attr('fill', 'none')
      .attr('stroke', '#d1d5db')
      .attr('stroke-width', 1.5);

    // Radius scale for anomaly markers based on deviation magnitude
    const deviationExtent = d3.extent(filteredAnomalies, (a) => {
      const val = Math.abs(a.zScore != null ? a.zScore : a.deviation || 0);
      return val;
    });
    const rScale = d3.scaleSqrt()
      .domain([0, deviationExtent[1] || 3])
      .range([4, 14])
      .clamp(true);

    // Draw anomaly markers
    filteredAnomalies.forEach((anomaly) => {
      const anomalyDate = new Date(anomaly.date);
      const cx = x(anomalyDate);
      const cy = y(anomaly.price);
      const magnitude = Math.abs(anomaly.zScore != null ? anomaly.zScore : anomaly.deviation || 1);
      const r = rScale(magnitude);
      const color = SEVERITY_COLORS[anomaly.severity] || '#6b7280';
      const dasharray = getStrokeDasharray(anomaly.type);

      g.append('circle')
        .attr('cx', cx)
        .attr('cy', cy)
        .attr('r', r)
        .attr('fill', color)
        .attr('fill-opacity', 0.25)
        .attr('stroke', color)
        .attr('stroke-width', 2)
        .attr('stroke-dasharray', dasharray)
        .style('cursor', 'pointer')
        .on('mouseover', (event) => {
          d3.select(event.currentTarget)
            .attr('fill-opacity', 0.5)
            .attr('stroke-width', 3);

          setTooltip({
            x: event.clientX + 12,
            y: event.clientY - 10,
            date: anomaly.date,
            address: anomaly.address,
            field: anomaly.field,
            type: anomaly.type,
            severity: anomaly.severity,
            price: anomaly.price,
            zScore: anomaly.zScore,
            deviation: anomaly.deviation,
          });
        })
        .on('mousemove', (event) => {
          setTooltip((prev) => prev ? {
            ...prev,
            x: event.clientX + 12,
            y: event.clientY - 10,
          } : null);
        })
        .on('mouseout', (event) => {
          d3.select(event.currentTarget)
            .attr('fill-opacity', 0.25)
            .attr('stroke-width', 2);
          setTooltip(null);
        });
    });

    // X axis
    g.append('g')
      .attr('transform', `translate(0, ${innerH})`)
      .call(
        d3.axisBottom(x)
          .ticks(Math.min(data.dates.length, 10))
          .tickSize(0)
          .tickFormat((d) => formatDateLabel(d.toISOString()))
      )
      .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280')
      .attr('transform', 'rotate(-30)')
      .attr('text-anchor', 'end')
      .attr('dx', '-0.5em')
      .attr('dy', '0.25em');

    // Y axis
    g.append('g')
      .call(
        d3.axisLeft(y)
          .ticks(6)
          .tickSize(0)
          .tickFormat((v) => `$${v.toFixed(0)}`)
      )
      .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280');

    // Y axis label
    g.append('text')
      .attr('transform', 'rotate(-90)')
      .attr('x', -innerH / 2)
      .attr('y', -40)
      .attr('text-anchor', 'middle')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280')
      .text('Price ($)');

    return () => {
      svg.selectAll('*').remove();
    };
  }, [data, filteredAnomalies]);

  // Empty state
  if (!data || !data.dates || data.dates.length === 0 || !data.priceLine || data.priceLine.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.header}>
          <div style={styles.title}>Price Anomaly Timeline</div>
          <div style={styles.subtitle}>
            Detected pricing anomalies plotted over time with severity indicators
          </div>
        </div>
        <div style={styles.emptyState}>No data available</div>
      </div>
    );
  }

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <div style={styles.title}>Price Anomaly Timeline</div>
        <div style={styles.subtitle}>
          Detected pricing anomalies plotted over time with severity indicators
        </div>
      </div>

      {/* Filter buttons */}
      <div style={styles.filterBar}>
        <span style={styles.filterLabel}>Filter:</span>
        {Object.keys(TYPE_LABELS).map((type) => (
          <button
            key={type}
            style={{
              ...styles.filterButton,
              ...(activeTypes[type] ? styles.filterButtonActive : {}),
            }}
            onClick={() => toggleType(type)}
          >
            {TYPE_LABELS[type]}
          </button>
        ))}
      </div>

      {/* Summary bar — severity pill counts */}
      <div style={styles.summaryBar}>
        {Object.entries(SEVERITY_COLORS).map(([severity, color]) => {
          const count = severityCounts[severity];
          if (count === 0) return null;
          return (
            <span
              key={severity}
              style={{
                ...styles.summaryPill,
                color: color,
                background: color + '18',
              }}
            >
              <span style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: color,
                display: 'inline-block',
                flexShrink: 0,
              }} />
              {count} {severity}
            </span>
          );
        })}
        {Object.values(severityCounts).every((c) => c === 0) && (
          <span style={{ fontSize: '12px', color: '#9ca3af' }}>No anomalies detected</span>
        )}
      </div>

      <div style={styles.container} ref={containerRef}>
        <svg ref={svgRef} />
      </div>

      {/* Tooltip */}
      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>
            {formatDateFull(tooltip.date)}
          </div>
          {tooltip.address && (
            <div style={styles.tooltipRow}>
              <span style={styles.tooltipLabel}>Address</span>
              <span style={styles.tooltipValue}>{tooltip.address}</span>
            </div>
          )}
          {tooltip.field && (
            <div style={styles.tooltipRow}>
              <span style={styles.tooltipLabel}>Field</span>
              <span style={styles.tooltipValue}>{tooltip.field}</span>
            </div>
          )}
          <div style={styles.tooltipRow}>
            <span style={styles.tooltipLabel}>Type</span>
            <span style={styles.tooltipValue}>
              {TYPE_LABELS[tooltip.type] || tooltip.type}
            </span>
          </div>
          <div style={styles.tooltipRow}>
            <span style={styles.tooltipLabel}>Severity</span>
            <span style={{
              fontWeight: 600,
              color: SEVERITY_COLORS[tooltip.severity] || '#6b7280',
            }}>
              {tooltip.severity}
            </span>
          </div>
          <div style={styles.tooltipRow}>
            <span style={styles.tooltipLabel}>Price</span>
            <span style={styles.tooltipValue}>
              ${tooltip.price != null ? tooltip.price.toFixed(2) : 'N/A'}
            </span>
          </div>
          {tooltip.zScore != null && (
            <div style={styles.tooltipRow}>
              <span style={styles.tooltipLabel}>Z-Score</span>
              <span style={{
                fontWeight: 600,
                color: Math.abs(tooltip.zScore) >= 3
                  ? '#7f1d1d'
                  : Math.abs(tooltip.zScore) >= 2
                  ? '#dc2626'
                  : '#f97316',
              }}>
                {tooltip.zScore >= 0 ? '+' : ''}{tooltip.zScore.toFixed(2)}
              </span>
            </div>
          )}
          {tooltip.deviation != null && (
            <div style={styles.tooltipRow}>
              <span style={styles.tooltipLabel}>Deviation</span>
              <span style={styles.tooltipValue}>
                {tooltip.deviation >= 0 ? '+' : ''}{tooltip.deviation.toFixed(2)}%
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
